/**
 * Sanitize data for SSR serialization
 * Removes undefined, NaN, Infinity, and other non-serializable values
 *
 * @param data - The data to sanitize
 * @returns Sanitized data safe for JSON serialization
 */
export function sanitizeForSerialization<T>(data: T): T {
  if (data === null || data === undefined) {
    return null as T
  }

  // Handle primitive types
  if (typeof data === 'number') {
    if (isNaN(data) || !isFinite(data)) {
      return 0 as T
    }
    return data
  }

  if (typeof data === 'string' || typeof data === 'boolean') {
    return data
  }

  // Handle Date objects
  if (data instanceof Date) {
    return data.getTime() as T
  }

  // Handle Arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForSerialization(item)) as T
  }

  // Handle Objects
  if (typeof data === 'object') {
    const sanitized: any = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key]
        // Skip undefined values
        if (value !== undefined) {
          sanitized[key] = sanitizeForSerialization(value)
        }
      }
    }
    return sanitized as T
  }

  return data
}
